<template>
  <div>
    <title-bar :title-stack="titleStack" />
    <section class="section is-main-section">
      <b-loading :active="isLoading" :is-full-page="false" />
      <div v-if="notFound" class="notification is-warning">
        Dosarul nu exista.
        <router-link :to="{ name: 'succession.files' }"
          >Inapoi la lista</router-link
        >
      </div>
      <template v-else-if="detail">
        <div class="card mb-4">
          <div class="card-content">
            <div class="level">
              <div class="level-left">
                <div>
                  <h1 class="title is-4">
                    {{ deceased }}
                    <span :class="['tag', 'is-medium', statusTag]">{{
                      statusLabel
                    }}</span>
                    <resdps-help-tip
                      :text="statusHelpText"
                      position="is-bottom"
                    />
                  </h1>
                  <p class="subtitle is-6">{{ registrationText }}</p>
                </div>
              </div>
              <div class="level-right">
                <succession-file-actions
                  :detail="detail"
                  :today="today"
                  @updated="onUpdated"
                  @stale="onStale"
                  @duplicate="onDuplicate"
                  @deleted="onDeleted"
                  @edit="edit"
                  @rectify="rectify"
                >
                  <succession-help />
                </succession-file-actions>
              </div>
            </div>
            <div class="tags are-medium">
              <span
                v-if="deadline.state !== 'none'"
                :class="['tag', deadlineTag]"
              >
                Termen transmitere: {{ date(detail.transmissionDeadline) }} ({{
                  deadlineText
                }})
                <resdps-help-tip
                  :text="helpTexts.transmissionDeadline"
                  position="is-bottom"
                />
              </span>
              <span class="tag is-light">
                Clasare posibila din:
                {{ date(detail.classificationAvailableFrom) }}
                <resdps-help-tip
                  :text="helpTexts.classification"
                  position="is-bottom"
                />
              </span>
              <span class="tag is-light"
                >Registratura generala: {{ generalRegistry }}</span
              >
              <span
                v-if="detail.chamberRegistrationNumber"
                class="tag is-light"
              >
                Camera: {{ chamberText }}
              </span>
            </div>
            <p class="is-size-7 has-text-grey">{{ traceText }}</p>
          </div>
        </div>

        <b-notification v-if="staleMessage" type="is-warning" :closable="false">
          {{ staleMessage }}
          <b-button size="is-small" class="ml-2" @click="load"
            >Reincarca dosarul</b-button
          >
        </b-notification>
        <b-notification
          v-if="missingLabels.length"
          type="is-warning"
          :closable="false"
        >
          Pentru inregistrare mai lipsesc:
          <strong>{{ missingLabels.join(", ") }}</strong>
          <b-button size="is-small" class="ml-2" @click="edit"
            >Completeaza</b-button
          >
        </b-notification>
        <b-notification v-if="duplicateLink" type="is-danger" :closable="false">
          Exista deja un dosar inregistrat pentru acest CNP:
          <router-link
            :to="{
              name: 'succession.file',
              params: { id: duplicateLink.successionFileId },
            }"
          >
            {{ duplicateLink.registration }}
          </router-link>
        </b-notification>

        <b-tabs v-model="activeTab" type="is-boxed" @input="onTabChange">
          <b-tab-item label="Date dosar">
            <succession-file-summary :detail="detail" />
          </b-tab-item>
          <b-tab-item :label="rectificationsLabel">
            <!-- Antetul tab-urilor are overflow: hidden si ar taia tooltip-ul. -->
            <template #header>
              <span>{{ rectificationsLabel }}</span>
              <resdps-help-tip
                :text="helpTexts.rectification"
                :append-to-body="true"
              />
            </template>
            <succession-file-rectifications
              :rectifications="detail.rectifications || []"
            />
          </b-tab-item>
          <b-tab-item :label="documentsLabel">
            <!-- Antetul tab-urilor are overflow: hidden si ar taia tooltip-ul. -->
            <template #header>
              <span>{{ documentsLabel }}</span>
              <resdps-help-tip
                :text="helpTexts.documents"
                :append-to-body="true"
              />
            </template>
            <succession-file-documents
              v-if="documentsOpened"
              :key="detail.id + '-' + detail.rowVersion"
              :file-id="detail.id"
            />
          </b-tab-item>
          <b-tab-item label="Istoric">
            <template #header>
              <span>Istoric</span>
              <resdps-help-tip
                :text="helpTexts.history"
                :append-to-body="true"
              />
            </template>
            <succession-file-history
              v-if="historyOpened"
              :key="detail.id + '-' + detail.rowVersion"
              :file-id="detail.id"
            />
          </b-tab-item>
        </b-tabs>
      </template>
    </section>
  </div>
</template>

<script>
import SuccessionService from "@/services/succession.service";
import { notify } from "@/helpers/notify";
import { describeError } from "@/helpers/resdpsErrors";
import {
  deadlineLabel,
  deadlineState,
  fieldLabel,
  formatDate,
  formatDateTime,
  formatDeceased,
  fromApiUtc,
  isTechnicalField,
  missingForRegistration,
  userLabel,
  startOfDay,
  statusLabel,
  statusTag,
} from "@/helpers/resdps";
import { CONCEPT_HELP, statusHelp } from "@/helpers/resdpsHelp";
import TitleBar from "@/components/TitleBar";
import ResdpsHelpTip from "@/components/succession/resdps-help-tip.vue";
import SuccessionHelp from "@/components/succession/succession-help.vue";
import SuccessionFileSummary from "@/components/succession/succession-file-summary.vue";
import SuccessionFileRectifications from "@/components/succession/succession-file-rectifications.vue";
import SuccessionFileActions from "@/components/succession/succession-file-actions.vue";
import SuccessionFileHistory from "@/components/succession/succession-file-history.vue";
import SuccessionFileDocuments from "@/components/succession/succession-file-documents.vue";

const DEADLINE_TAGS = { late: "is-danger", soon: "is-warning" };

export default {
  name: "SuccessionFile",
  components: {
    TitleBar,
    ResdpsHelpTip,
    SuccessionHelp,
    SuccessionFileSummary,
    SuccessionFileRectifications,
    SuccessionFileActions,
    SuccessionFileHistory,
    SuccessionFileDocuments,
  },
  data() {
    return {
      helpTexts: {
        transmissionDeadline: CONCEPT_HELP.transmissionDeadline.text,
        classification: CONCEPT_HELP.classification.text,
        rectification: CONCEPT_HELP.rectification.text,
        history: CONCEPT_HELP.history.text,
        documents: CONCEPT_HELP.documents.text,
      },
      detail: null,
      isLoading: false,
      notFound: false,
      staleMessage: null,
      duplicate: null,
      activeTab: 0,
      historyOpened: false,
      documentsOpened: false,
      today: startOfDay(new Date()),
    };
  },
  computed: {
    id() {
      return Number(this.$route.params.id);
    },
    isDraft() {
      return !!this.detail && this.detail.status === "Draft";
    },
    deceased() {
      return formatDeceased(this.detail);
    },
    statusLabel() {
      return statusLabel(this.detail.status);
    },
    statusTag() {
      return statusTag(this.detail.status);
    },
    statusHelpText() {
      return statusHelp(this.detail.status);
    },
    registrationText() {
      return this.detail.registration
        ? "R.E.S.D.P.S. nr. " +
            this.detail.registration +
            " din " +
            formatDate(this.detail.registrationDate)
        : "Ciorna, fara numar in R.E.S.D.P.S.";
    },
    deadline() {
      return deadlineState(this.detail.transmissionDeadline, this.today);
    },
    deadlineTag() {
      return DEADLINE_TAGS[this.deadline.state] || "is-light";
    },
    deadlineText() {
      return deadlineLabel(this.deadline);
    },
    generalRegistry() {
      return this.detail.generalRegistryNumber
        ? this.detail.generalRegistryNumber +
            " / " +
            formatDate(this.detail.generalRegistryDate)
        : "—";
    },
    chamberText() {
      return (
        (this.detail.notaryChamberName || "—") +
        ", nr. " +
        this.detail.chamberRegistrationNumber +
        " / " +
        formatDate(this.detail.chamberRegistrationDate)
      );
    },
    traceText() {
      const created =
        "Creat de " +
        userLabel(this.detail.createdByEmail, this.detail.createdBySub) +
        " la " +
        formatDateTime(fromApiUtc(this.detail.createdAtUtc));
      if (!this.detail.modifiedAtUtc) return created;
      return (
        created +
        "; modificat de " +
        userLabel(this.detail.modifiedByEmail, this.detail.modifiedBySub) +
        " la " +
        formatDateTime(fromApiUtc(this.detail.modifiedAtUtc))
      );
    },
    missingLabels() {
      return this.isDraft
        ? missingForRegistration(this.detail).map(fieldLabel)
        : [];
    },
    duplicateLink() {
      if (this.duplicate) return this.duplicate;
      if (this.isDraft && this.detail.duplicateCnpSuccessionFileId) {
        return {
          successionFileId: this.detail.duplicateCnpSuccessionFileId,
          registration: this.detail.duplicateCnpRegistration,
        };
      }
      return null;
    },
    rectificationsLabel() {
      const visible = (this.detail.rectifications || []).filter(
        (row) => !isTechnicalField(row.fieldName)
      );
      return "Rectificari (" + visible.length + ")";
    },
    documentsLabel() {
      return "Documente (" + (this.detail.documentCount || 0) + ")";
    },
    titleStack() {
      const parent = { label: "Succesiuni", to: "/succesiuni" };
      if (!this.detail) return [parent, "Dosar"];
      return [parent, this.detail.registration || "Ciorna"];
    },
  },
  watch: {
    "$route.params.id"(id, previous) {
      if (id !== previous) this.init();
    },
  },
  created() {
    this.init();
  },
  methods: {
    // Fiecare citire a dosarului scrie ViewedFile in jurnal (spec B); dupa salvare sau actiune dosarul vine prin ruta.
    init() {
      this.staleMessage = null;
      this.duplicate = null;
      this.notFound = false;
      this.activeTab = 0;
      this.historyOpened = false;
      this.documentsOpened = false;
      const passed = this.$route.params.detail;
      if (passed && String(passed.id) === String(this.$route.params.id)) {
        this.detail = passed;
        return;
      }
      this.detail = null;
      this.load();
    },
    async load() {
      this.isLoading = true;
      try {
        this.detail = await SuccessionService.getFile(this.id);
        this.notFound = false;
        this.staleMessage = null;
        this.duplicate = null;
      } catch (error) {
        const described = describeError(error);
        if (described.status === 404) {
          this.notFound = true;
          this.detail = null;
        } else {
          notify.error(this, described.message);
        }
      } finally {
        this.isLoading = false;
      }
    },
    // Documentele si istoricul se cer abia cand tab-ul e deschis; dupa o actiune cheia (rowVersion)
    // le reincarca.
    onTabChange(index) {
      if (index === 2) this.documentsOpened = true;
      if (index === 3) this.historyOpened = true;
    },
    onUpdated(detail) {
      this.detail = detail;
      this.staleMessage = null;
      this.duplicate = null;
    },
    onStale(message) {
      this.staleMessage = message;
    },
    onDuplicate(duplicate) {
      this.duplicate = duplicate;
    },
    onDeleted() {
      this.$router.push({ name: "succession.files" });
    },
    edit() {
      this.$router.push({
        name: "succession.edit",
        params: { id: this.detail.id, detail: this.detail },
      });
    },
    rectify() {
      this.$router.push({
        name: "succession.rectify",
        params: { id: this.detail.id, detail: this.detail },
      });
    },
    date: formatDate,
  },
};
</script>
